/**
 * FleetA Library - Main Export
 * Central export point for all library functionality
 */

// === API MODULE ===
export * from './api';

// === AUTH MODULE ===
export * from './auth';

// === DEVICE MODULE ===
export * from './device';

// === LIVE VIEW MODULE ===
export {
  // Live API Functions
  playLiveView,
  stopLiveView,
  changeLiveDirection,
  changeLiveDirectionWithRetry,
  playLiveViewWithRetry,
  getLivePresignURL,
  startLiveStreamingWorkflow,
  createCustomSigner,
  validateWebRTCConfig,
  getLiveStreamStatus,
  startTwoWayAudio,
  stopTwoWayAudio,
  
  // Live Types (avoiding conflicts)
  type LiveCameraChannel,
  type LiveStreamStatus,
  type LiveStreamState,
  type PlayLiveViewParams,
  type PlayLiveViewResponse,
  type StopLiveViewParams,
  type StopLiveViewResponse,
  type LiveDirectionParams,
  type LiveDirectionResponse,
  type LivePresignURLParams,
  type LivePresignURLResponse,
  type LiveStreamingWorkflow,
  type TwoWayStatus,
  type TwoWayStartParams,
  type TwoWayStartResponse,
  type TwoWayStopParams,
  type TwoWayStopResponse,
  
  // Constants
  LIVE_VIEW_ERROR_MESSAGES,
  CAMERA_CHANNEL_NAMES
} from './live';

// === LIVE PROVIDER MODULE (Primary WebRTC API) ===
export {
  // Primary WebRTC Provider API (Client-facing)
  WebRTCProvider,
  createWebRTCProvider,
  
  // Media Device Utilities
  checkMediaDeviceSupport,
  requestMicrophonePermission,
  
  // WebRTC Connection Management
  WebRTCConnection,
  
  // Factory and Supporting Classes
  WebRTCFactory,
  AudioManager,
  ConnectionStateMachine,
  
  // SDK Loader
  GenericSDKLoader,
  createWebRTCSDKLoader,
  
  // Provider Types (Primary - take precedence over live types)
  type WebRTCProviderConfig,
  type WebRTCProviderCallbacks,
  type ConnectionState,
  type ConnectionStats,
  type ICEServer, // Provider version takes precedence
  type EndpointsByProtocol, // Provider version takes precedence
  type WebRTCConfig, // Provider version takes precedence (internal)
  type WebRTCConnectionConfig,
  type WebRTCConnectionEvents,
  type MediaDeviceCapabilities,
  type ConnectionEvent,
  type ISDKLoader,
  type SDKLoaderConfig
} from './live-provider';

// === STORE MODULE ===
export { useAuthStore } from './store/authStore';
export { useApiEndpointStore } from './store/apiEndpointStore';

// === GPS WEBSOCKET MODULE ===
export {
  // GPS WebSocket SDK (로우레벨 API)
  GpsWebSocketSDK,
  GpsWebSocketFactory,
  
  // Individual managers for advanced usage
  ConnectionManager,
  AuthManager as GpsAuthManager,
  EventManager as GpsEventManager,
  RequestManager as GpsRequestManager,
  GpsConnectionStateMachine,
  
  // GPS WebSocket Types
  type GpsWebSocketSDKOptions,
  type GpsWsEvent,
  type GpsWsEventHandler,
  type GpsConnectionState,
  type DeviceGpsInfo,
  type GpsWebSocketRequest,
  type GpsWebSocketResponse
} from './gps-websocket';

// === GPS DATA PROVIDER MODULE (High-level GPS API) ===
export {
  // GPS Data Provider Classes
  GpsDataProvider,
  GpsDataManager,
  
  // Factory Functions (Primary GPS API)
  createBasicGpsProvider,
  createMapGpsProvider,
  createDashboardGpsProvider,
  createZoneGpsProvider,
  createPublicGpsProvider,
  
  // GPS Data Utilities
  hasSignificantChange,
  processGpsDataUpdates,
  optimizeForMapRendering,
  calculateProcessingMetrics,
  batchProcessUpdates,
  
  // Filtering utilities
  applyAutoFilter,
  applySubscriberFilter,
  filterByStatus,
  filterByRadius,
  filterBySpeed,
  groupDevices,
  sortDevices,
  findNearbyDevices,
  isValidCoordinate,
  isWithinBoundingBox,
  createCombinedFilter,
  createSearchFilter,
  
  // Caching utilities
  GpsDataCache,
  createGpsDataCache,
  generateCacheKey,
  CacheKeys,
  
  // GPS Data Provider Types
  type GpsDataProviderConfig,
  type GpsDataStats,
  type GpsDataProviderState,
  type DataProcessingResult,
  DEFAULT_GPS_PROVIDER_CONFIG
} from './gps-data-provider';

// === EVENT WEBSOCKET MODULE ===
export {
  // Event WebSocket SDK for WebPush notifications
  EventWebSocketSDK,
  
  // Individual managers for advanced usage
  EventConnectionManager,
  EventWebSocketEventManager,
  
  // Event WebSocket Types
  type EventWebSocketSDKOptions,
  type EventWsEvent,
  type EventWsEventHandler,
  type EventConnectionState,
  type WebPushMessage,
  type WebPushEventCode,
  type MessageOnlyEventCode,
  type ImageIncludedEventCode,
  
  // Utility functions
  isImageIncludedEvent,
  WEBPUSH_EVENT_DESCRIPTIONS,
  WEBPUSH_EVENT_CATEGORIES,
  DEFAULT_EVENT_WEBSOCKET_CONFIG
} from './event-websocket';

// === MULTI-DEVICE PROVIDER MODULE ===
export {
  // Main Provider Classes
  MultiDeviceProvider,
  DeviceStreamManager,
  StreamConnectionPool,
  MultiDeviceWebRTCConnection,
  QualityManager,
  createQualityManager,
  
  // Factory Functions (Primary Multi-Device API)
  createMultiDeviceProvider,
  createGrid2x2Provider,
  createGrid3x3Provider,
  createGrid4x4Provider,
  
  // Multi-Device Provider Types
  type MultiDeviceProviderConfig,
  type DeviceManagerConfig,
  type MultiDeviceConnectionConfig,
  type ConnectionState as MultiDeviceConnectionState,
  type StreamQuality,
  type StreamType,
  type CameraChannel,
  type StreamUpdateEvent,
  type StreamCallback,
  type DeviceManagerEvents,
  type MultiDeviceProviderEvents,
  type VoiceCommState,
  type TwoWayAudioStatus,
  type DeviceStats,
  type MultiDeviceStats,
  type PooledConnection,
  type PoolStats,
  
  // Constants
  DEFAULT_MULTI_DEVICE_CONFIG
} from './multi-device-provider';

// === USERS MODULE ===
export {
  // Users API Functions
  inviteMember,
  getInviteMemberList,
  deleteMember,
  inviteSubMaster,
  inviteUser,
  isUsersApiReady,
  getMasterPermissions,
  getUserPermissions,
  getTargetUserPermissions,
  setTargetUserPermissions,
  hasPermission,
  
  // Users Auth Utilities
  getAuthConfigFromStore as getUsersAuthConfigFromStore,
  isAuthConfigured as isUsersAuthConfigured,
  isAuthConfigValid as isUsersAuthConfigValid,
  getAuthHeaders as getUsersAuthHeaders,
  
  // Users Types
  type InviteMemberParams,
  type InviteMemberResponse,
  type GetInviteMemberListParams,
  type GetInviteMemberListResponse,
  type DeleteMemberParams,
  type DeleteMemberResponse,
  type GetTargetUserPermissionsParams,
  type GetTargetUserPermissionsResponse,
  type SetTargetUserPermissionsParams,
  type SetTargetUserPermissionsResponse,
  type InvitedMemberInfo,
  type InvitedMemberGroup,
  type FleetUserType,
  type LoginApp,
  
  // Users Constants
  INVITE_MEMBER_ERROR_MESSAGES,
  GET_INVITE_MEMBER_ERROR_MESSAGES,
  DELETE_MEMBER_ERROR_MESSAGES,
  GET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES,
  SET_TARGET_USER_PERMISSIONS_ERROR_MESSAGES,
  FLEET_USER_TYPE_NAMES,
  LOGIN_APP_NAMES,
  MAIL_STATUS_NAMES,
  USER_ROLE_NAMES,
  PERMISSION_DISPLAY_NAMES,
  PERMISSION_CATEGORIES,
  
  // Users Version Info
  USERS_API_VERSION,
  USERS_API_NAME
} from './users';

// === GROUP MODULE ===
export * from './group';

// === VERSION INFO ===
export const LIB_VERSION = '1.4.0';
export const LIB_NAME = 'FleetA Library';

// === DEFAULT EXPORT ===
export default {
  version: LIB_VERSION,
  name: LIB_NAME
};